import { useState, useEffect } from "react"
import { motion, AnimatePresence } from "framer-motion"
import { Search, SlidersHorizontal, Grid3X3, List, X, Check, RotateCcw } from "lucide-react"
import { Input } from "@/components/ui/input"
import { Button } from "@/components/ui/button"
import { Badge } from "@/components/ui/badge"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger, SheetClose } from "@/components/ui/sheet"
import { Slider } from "@/components/ui/slider"
import { Checkbox } from "@/components/ui/checkbox"
import { ProductCard } from "@/components/product-card"
import { useLanguage } from "@/lib/language-context"
import { getProducts, getCategories, type Product, type Category } from "@/lib/api"


export function ProductsContent() {
  const { t } = useLanguage()
  const [products, setProducts] = useState<Product[]>([])
  const [categories, setCategories] = useState<Category[]>([])
  const [loading, setLoading] = useState(true)
  const [searchQuery, setSearchQuery] = useState("")
  const [selectedCategory, setSelectedCategory] = useState<string>("all")
  const [sortBy, setSortBy] = useState("newest")
  const [viewMode, setViewMode] = useState<"grid" | "list">("grid")
  const [priceRange, setPriceRange] = useState([0, 1000000])
  const [showNewOnly, setShowNewOnly] = useState(false)
  const [showPopularOnly, setShowPopularOnly] = useState(false)
  const [isFilterOpen, setIsFilterOpen] = useState(false)

  // Count active filters
  const activeFiltersCount = [
    selectedCategory !== "all",
    priceRange[0] > 0 || priceRange[1] < 1000000,
    showNewOnly,
    showPopularOnly,
  ].filter(Boolean).length

  useEffect(() => {
    async function fetchData() {
      try {
        const [productsData, categoriesData] = await Promise.all([getProducts(), getCategories()])
        setProducts(productsData || [])
        setCategories(categoriesData || [])
      } catch (error) {
        console.error("Error fetching data:", error)
        setProducts([])
        setCategories([])
      } finally {
        setLoading(false)
      }
    }
    fetchData()
  }, [])

  // Filter and sort products
  const filteredProducts = (products || [])
    .filter((product) => {
      // Search filter
      if (searchQuery && !product.name.toLowerCase().includes(searchQuery.toLowerCase())) {
        return false
      }
      // Category filter
      if (selectedCategory !== "all" && product.category !== Number.parseInt(selectedCategory)) {
        return false
      }
      // Price filter - convert string to number for comparison
      const numericPrice = typeof product.price === 'string' ? parseFloat(product.price) : product.price
      if (numericPrice < priceRange[0] || numericPrice > priceRange[1]) {
        return false
      }
      // New only filter
      if (showNewOnly && !(product as unknown as { is_new?: boolean }).is_new) {
        return false
      }
      // Popular only filter
      if (showPopularOnly && !(product as unknown as { is_popular?: boolean }).is_popular) {
        return false
      }
      return true
    })
    .sort((a, b) => {
      switch (sortBy) {
        case "price_low":
          return (typeof a.price === 'string' ? parseFloat(a.price) : a.price) - (typeof b.price === 'string' ? parseFloat(b.price) : b.price)
        case "price_high":
          return (typeof b.price === 'string' ? parseFloat(b.price) : b.price) - (typeof a.price === 'string' ? parseFloat(a.price) : a.price)
        case "name_az":
          return a.name.localeCompare(b.name)
        case "name_za":
          return b.name.localeCompare(a.name)
        case "newest":
        default:
          return b.id - a.id
      }
    })

  const formatPrice = (price: number) => {
    return new Intl.NumberFormat("uz-UZ").format(price) + " UZS"
  }

  const clearFilters = () => {
    setSelectedCategory("all")
    setPriceRange([0, 1000000])
    setShowNewOnly(false)
    setShowPopularOnly(false)
  }

  if (loading) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
          {[...Array(8)].map((_, i) => (
            <div key={i} className="bg-muted animate-pulse rounded-xl h-80" />
          ))}
        </div>
      </div>
    )
  }

  // Filter Panel Component (reusable for desktop and mobile)
  const FilterPanel = ({ isMobile = false }: { isMobile?: boolean }) => (
    <div className={`space-y-6 ${isMobile ? '' : ''}`}>
      {/* Categories */}
      <div className={isMobile ? "" : "bg-card rounded-xl p-5 border border-border"}>
        <h3 className="font-semibold text-foreground mb-4">{t("nav", "categories")}</h3>
        <div className="space-y-1">
          <button
            onClick={() => setSelectedCategory("all")}
            className={`w-full text-left px-3 py-2.5 rounded-lg transition-all flex items-center justify-between ${selectedCategory === "all"
              ? "bg-primary text-primary-foreground shadow-md"
              : "hover:bg-muted text-muted-foreground"
              }`}
          >
            <span>{t("products", "all_categories") || "Barcha kategoriyalar"}</span>
            {selectedCategory === "all" && <Check className="w-4 h-4" />}
          </button>
          {(categories || []).map((category) => (
            <button
              key={category.id}
              onClick={() => setSelectedCategory(category.id.toString())}
              className={`w-full text-left px-3 py-2.5 rounded-lg transition-all flex items-center justify-between ${selectedCategory === category.id.toString()
                ? "bg-primary text-primary-foreground shadow-md"
                : "hover:bg-muted text-muted-foreground"
                }`}
            >
              <span className="flex items-center gap-2">
                {category.name}
                {category.products_count !== undefined && (
                  <Badge variant="secondary" className="text-xs px-1.5 py-0">
                    {category.products_count}
                  </Badge>
                )}
              </span>
              {selectedCategory === category.id.toString() && <Check className="w-4 h-4" />}
            </button>
          ))}
        </div>
      </div>

      {/* Price Range */}
      <div className={isMobile ? "" : "bg-card rounded-xl p-5 border border-border"}>
        <h3 className="font-semibold text-foreground mb-4">
          {t("products", "price_range") || "Narx oralig'i"}
        </h3>
        <Slider value={priceRange} onValueChange={setPriceRange} max={1000000} step={10000} className="mb-4" />
        <div className="flex items-center justify-between text-sm">
          <span className="bg-muted px-2 py-1 rounded text-muted-foreground">{formatPrice(priceRange[0])}</span>
          <span className="text-muted-foreground">—</span>
          <span className="bg-muted px-2 py-1 rounded text-muted-foreground">{formatPrice(priceRange[1])}</span>
        </div>
      </div>

      {/* Additional Filters */}
      <div className={isMobile ? "" : "bg-card rounded-xl p-5 border border-border"}>
        <h3 className="font-semibold text-foreground mb-4">{t("products", "filters") || "Filtrlar"}</h3>
        <div className="space-y-3">
          <label className="flex items-center gap-3 cursor-pointer p-2 rounded-lg hover:bg-muted transition-colors">
            <Checkbox checked={showNewOnly} onCheckedChange={(checked) => setShowNewOnly(checked as boolean)} />
            <span className="text-sm text-foreground">
              {t("products", "new_only") || "Faqat yangilar"}
            </span>
          </label>
          <label className="flex items-center gap-3 cursor-pointer p-2 rounded-lg hover:bg-muted transition-colors">
            <Checkbox
              checked={showPopularOnly}
              onCheckedChange={(checked) => setShowPopularOnly(checked as boolean)}
            />
            <span className="text-sm text-foreground">
              {t("products", "popular_only") || "Faqat mashhurlar"}
            </span>
          </label>
        </div>
      </div>

      {/* Clear Filters Button - only show if filters active */}
      {activeFiltersCount > 0 && (
        <Button
          variant="outline"
          className="w-full gap-2"
          onClick={clearFilters}
        >
          <RotateCcw className="w-4 h-4" />
          {t("products", "clear_filters") || "Filtrlarni tozalash"}
        </Button>
      )}
    </div>
  )

  return (
    <div className="min-h-screen bg-background">
      {/* Page Header */}
      <div className="bg-gradient-to-r from-primary/10 via-primary/5 to-transparent py-12">
        <div className="container mx-auto px-4">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
            <h1 className="text-3xl md:text-4xl font-bold text-foreground mb-2">{t("nav", "products")}</h1>
            <p className="text-muted-foreground">{t("home", "products_subtitle") || "Barcha tabiiy mahsulotlarimiz"}</p>
            <div className="flex items-center gap-2 mt-4">
              <Badge variant="secondary">
                {filteredProducts.length} {t("products", "items") || "ta mahsulot"}
              </Badge>
              {activeFiltersCount > 0 && (
                <Badge variant="outline" className="text-primary border-primary">
                  {activeFiltersCount} {t("products", "active_filters") || "ta filtr faol"}
                </Badge>
              )}
            </div>
          </motion.div>
        </div>
      </div>

      <div className="container mx-auto px-4 py-8">
        <div className="flex flex-col lg:flex-row gap-8">
          {/* Sidebar Filters - Desktop */}
          <aside className="hidden lg:block w-64 shrink-0">
            <div className="sticky top-24">
              <FilterPanel isMobile={false} />
            </div>
          </aside>

          {/* Main Content */}
          <div className="flex-1">
            {/* Toolbar */}
            <div className="flex flex-col sm:flex-row gap-4 mb-6">
              {/* Search */}
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder={t("header", "search_placeholder") || "Mahsulotlarni qidirish..."}
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10"
                />
              </div>

              {/* Mobile Filter Button */}
              <Sheet open={isFilterOpen} onOpenChange={setIsFilterOpen}>
                <SheetTrigger asChild>
                  <Button variant="outline" className="lg:hidden bg-card relative">
                    <SlidersHorizontal className="h-4 w-4 mr-2" />
                    {t("products", "filters") || "Filtrlar"}
                    {activeFiltersCount > 0 && (
                      <span className="absolute -top-2 -right-2 w-5 h-5 bg-primary text-primary-foreground text-xs rounded-full flex items-center justify-center font-bold">
                        {activeFiltersCount}
                      </span>
                    )}
                  </Button>
                </SheetTrigger>
                <SheetContent side="bottom" className="h-[85vh] rounded-t-3xl">
                  <div className="flex flex-col h-full">
                    <SheetHeader className="pb-4 border-b">
                      <div className="flex items-center justify-between">
                        <SheetTitle className="flex items-center gap-2">
                          <SlidersHorizontal className="w-5 h-5" />
                          {t("products", "filters") || "Filtrlar"}
                          {activeFiltersCount > 0 && (
                            <Badge variant="secondary">{activeFiltersCount}</Badge>
                          )}
                        </SheetTitle>
                        <SheetClose asChild>
                          <Button variant="ghost" size="icon" className="rounded-full">
                            <X className="w-5 h-5" />
                          </Button>
                        </SheetClose>
                      </div>
                    </SheetHeader>

                    {/* Scrollable Filter Content */}
                    <div className="flex-1 overflow-y-auto py-6 px-1">
                      <FilterPanel isMobile={true} />
                    </div>

                    {/* Fixed Bottom Buttons */}
                    <div className="pt-4 border-t bg-background flex gap-3">
                      <Button
                        variant="outline"
                        className="flex-1 gap-2"
                        onClick={clearFilters}
                      >
                        <RotateCcw className="w-4 h-4" />
                        {t("products", "clear") || "Tozalash"}
                      </Button>
                      <SheetClose asChild>
                        <Button className="flex-1 gap-2">
                          <Check className="w-4 h-4" />
                          {t("products", "show_results") || "Ko'rsatish"} ({filteredProducts.length})
                        </Button>
                      </SheetClose>
                    </div>
                  </div>
                </SheetContent>
              </Sheet>

              {/* Sort */}
              <Select value={sortBy} onValueChange={setSortBy}>
                <SelectTrigger className="w-full sm:w-48">
                  <SelectValue placeholder={t("products", "sort_by") || "Saralash"} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="newest">{t("products", "sort_newest") || "Eng yangi"}</SelectItem>
                  <SelectItem value="price_low">{t("products", "sort_price_low") || "Arzon - Qimmat"}</SelectItem>
                  <SelectItem value="price_high">{t("products", "sort_price_high") || "Qimmat - Arzon"}</SelectItem>
                  <SelectItem value="name_az">{t("products", "sort_name_az") || "A - Z"}</SelectItem>
                  <SelectItem value="name_za">{t("products", "sort_name_za") || "Z - A"}</SelectItem>
                </SelectContent>
              </Select>

              {/* View Mode */}
              <div className="hidden sm:flex items-center gap-1 bg-muted rounded-lg p-1">
                <Button
                  variant={viewMode === "grid" ? "secondary" : "ghost"}
                  size="icon"
                  className="h-8 w-8"
                  onClick={() => setViewMode("grid")}
                >
                  <Grid3X3 className="h-4 w-4" />
                </Button>
                <Button
                  variant={viewMode === "list" ? "secondary" : "ghost"}
                  size="icon"
                  className="h-8 w-8"
                  onClick={() => setViewMode("list")}
                >
                  <List className="h-4 w-4" />
                </Button>
              </div>
            </div>

            {/* Active Filters Tags - Mobile */}
            <AnimatePresence>
              {activeFiltersCount > 0 && (
                <motion.div
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: "auto" }}
                  exit={{ opacity: 0, height: 0 }}
                  className="flex flex-wrap gap-2 mb-4 lg:hidden"
                >
                  {selectedCategory !== "all" && (
                    <Badge
                      variant="secondary"
                      className="gap-1 cursor-pointer hover:bg-destructive hover:text-destructive-foreground"
                      onClick={() => setSelectedCategory("all")}
                    >
                      {categories.find(c => c.id.toString() === selectedCategory)?.name}
                      <X className="w-3 h-3" />
                    </Badge>
                  )}
                  {(priceRange[0] > 0 || priceRange[1] < 1000000) && (
                    <Badge
                      variant="secondary"
                      className="gap-1 cursor-pointer hover:bg-destructive hover:text-destructive-foreground"
                      onClick={() => setPriceRange([0, 1000000])}
                    >
                      {formatPrice(priceRange[0])} - {formatPrice(priceRange[1])}
                      <X className="w-3 h-3" />
                    </Badge>
                  )}
                  {showNewOnly && (
                    <Badge
                      variant="secondary"
                      className="gap-1 cursor-pointer hover:bg-destructive hover:text-destructive-foreground"
                      onClick={() => setShowNewOnly(false)}
                    >
                      {t("products", "new_only") || "Yangilar"}
                      <X className="w-3 h-3" />
                    </Badge>
                  )}
                  {showPopularOnly && (
                    <Badge
                      variant="secondary"
                      className="gap-1 cursor-pointer hover:bg-destructive hover:text-destructive-foreground"
                      onClick={() => setShowPopularOnly(false)}
                    >
                      {t("products", "popular_only") || "Mashhurlar"}
                      <X className="w-3 h-3" />
                    </Badge>
                  )}
                </motion.div>
              )}
            </AnimatePresence>

            {/* Products Grid */}
            {filteredProducts.length > 0 ? (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.3 }}
                className={
                  viewMode === "grid" ? "grid grid-cols-2 sm:grid-cols-2 xl:grid-cols-3 gap-3 sm:gap-6" : "flex flex-col gap-4"
                }
              >
                {filteredProducts.map((product, index) => (
                  <motion.div
                    key={product.id}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.3, delay: index * 0.05 }}
                  >
                    <ProductCard product={product} viewMode={viewMode} />
                  </motion.div>
                ))}
              </motion.div>
            ) : (
              <div className="text-center py-16">
                <div className="w-24 h-24 bg-muted rounded-full flex items-center justify-center mx-auto mb-4">
                  <Search className="h-10 w-10 text-muted-foreground" />
                </div>
                <h3 className="text-lg font-semibold text-foreground mb-2">
                  {t("products", "no_products") || "Mahsulotlar topilmadi"}
                </h3>
                <p className="text-muted-foreground mb-4">
                  {t("products", "try_different_filter") || "Boshqa filtrlarni sinab ko'ring"}
                </p>
                <Button
                  variant="outline"
                  onClick={() => {
                    setSearchQuery("")
                    clearFilters()
                  }}
                >
                  {t("products", "clear_filters") || "Filtrlarni tozalash"}
                </Button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Floating Filter Button - Mobile Only */}
      <AnimatePresence>
        {!isFilterOpen && (
          <motion.div
            initial={{ opacity: 0, scale: 0.8, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.8, y: 20 }}
            className="lg:hidden fixed bottom-6 right-6 z-50"
          >
            <Button
              size="lg"
              className="rounded-full shadow-xl h-14 w-14 p-0"
              onClick={() => setIsFilterOpen(true)}
            >
              <SlidersHorizontal className="w-5 h-5" />
              {activeFiltersCount > 0 && (
                <span className="absolute -top-1 -right-1 w-5 h-5 bg-destructive text-destructive-foreground text-xs rounded-full flex items-center justify-center font-bold">
                  {activeFiltersCount}
                </span>
              )}
            </Button>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  )
}

